import { Block, Button, DataTable, DataTableRef, SettingsIcon } from '@uni-space/uni-components';
import { getAllBags, getSortAllBags, getSortedBags } from '../api';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import './ManualSorting.scss';
import SlidingBag from './SlidingBag';
import CreateBag from './CreateBag';
import BagsSettingsModal from './BagsSettingsModal';
import { useBagsSettingsTable } from '../model/useBagsSettingsTable';
import { ensureStarted, getSortingCenterHub } from '@shared/config/signalR/SortingSettingsHub';

export default function ManualSorting({ isSortingTapeBags = false }: { isSortingTapeBags?: boolean }) {
  const [isOpenSlider, setIsOpenSlider] = useState(false);
  const [showAll, setShowAll] = useState<boolean>(false);
  const { columns, filters, sliderState, handleResetState, handleOpenDetails, loadingDetails } = useBagsSettingsTable({
    openSlider: () => setIsOpenSlider(true),
    isSortingTapeBags,
  });
  const { i18n, t } = useTranslation();
  const tableRef = useRef<DataTableRef>(null);
  const showAllRef = useRef<boolean>(false);
  const [openModal, setOpenModal] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  const { data: bagCodesData, isLoading: isLoadingBagCode } = useQuery({
    queryKey: ['getBagCodes'],
    queryFn: getSortedBags,
  });

  const handleRefetch = () => {
    tableRef.current?.refetchData();
  };

  const handleToggleShowAll = () => {
    const next = !showAllRef.current;
    showAllRef.current = next;
    setShowAll(next);
    tableRef.current?.refetchData();
  };

  useEffect(() => {
    if (!isSortingTapeBags) return;

    const conn = getSortingCenterHub();

    const BagCreated = () => {
      handleRefetch();
    };
    const BagClosed = () => {
      handleRefetch();
    };
    const onChuteOpened = () => {
      handleRefetch();
    };
    const onChuteClosed = () => {
      handleRefetch();
    };

    conn.on('BagCreated', BagCreated);
    conn.on('BagClosed', BagClosed);
    conn.on('ChuteOpened', onChuteOpened);
    conn.on('ChuteClosed', onChuteClosed);

    conn.onreconnecting((err) => console.warn('SignalR reconnecting…', err?.message));
    conn.onreconnected((newId) => console.info('SignalR reconnected', newId));
    conn.onclose((err) => console.error('SignalR closed', err?.message));

    (async () => {
      await ensureStarted(conn);
      console.info('SignalR connected', conn.connectionId);
    })();

    return () => {
      conn.off('BagCreated', BagCreated);
      conn.off('BagClosed', BagClosed);
      conn.off('ChuteOpened', onChuteOpened);
      conn.off('ChuteClosed', onChuteClosed);
    };
  }, []);

  return (
    <>
      <DataTable
        renderControls={
          isSortingTapeBags ? null : (
            <Block sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Button onClick={handleToggleShowAll} size="s">
                {showAll ? t('bagsControls.showOnlyMine') : t('bagsControls.showAll')}
              </Button>
              <Button onClick={() => setOpenModal(true)} size="s">
                {t('createBagTitle')}
              </Button>
              <Button onClick={() => setOpenSettings(true)} size="s" type="secondary">
                <SettingsIcon size="s" />
              </Button>
            </Block>
          )
        }
        columns={columns}
        filters={filters}
        height="calc(100vh - 180px)"
        onFetch={
          isSortingTapeBags
            ? getSortAllBags
            : (arg1, arg2, arg3, ctx) => getAllBags(arg1, arg2, arg3, showAllRef.current, ctx)
        }
        lang={i18n.language}
        ref={tableRef}
      />
      <BagsSettingsModal isOpen={openSettings} onClose={() => setOpenSettings(false)} />
      <CreateBag
        isOpenModal={openModal}
        onChangeOpenModal={(isOpen) => setOpenModal(isOpen)}
        onRefetchTable={tableRef.current?.refetchData}
        isLoading={isLoadingBagCode}
        bagCodes={bagCodesData?.items ?? []}
      />
      <SlidingBag
        state={sliderState}
        isSortingTapeBags={isSortingTapeBags}
        isOpen={isOpenSlider}
        onRefetchTable={tableRef.current?.refetchData}
        handleOpenDetails={handleOpenDetails}
        loadingDetails={loadingDetails}
        onClose={() => {
          handleResetState();
          setIsOpenSlider(false);
        }}
      />
    </>
  );
}
